import { useModals } from "@/features/modals/hooks";
import {
  type Appointment,
  asociateAppointment,
  dissasociateAppointment,
} from "@repo/api";
import { t } from "@repo/translation";
import { ModalSheet, useToast } from "@repo/ui";
import { getErrorMessage, useTypesafeActions } from "@repo/utils";

import {
  type SearchAppointmentState,
  type SearchAppointmentStateActions,
  SearchAppointmentStateDefault,
  searchAppointmentStateActions,
  searchAppointmentStateReducer,
} from "./SearchAppointmentReducer";
import { SearchAppointments } from "./SearchAppointments";

interface AppointmentModalProps {
  isOpen: boolean;
  closeModal: () => void;
  projectId: number;
  initialSearchTerm?: string;
  initialAppointments?: Appointment[];
  onBack?: () => void;
}

export const SearchAppointmentModal = ({
  isOpen,
  closeModal,
  projectId,
  initialSearchTerm,
  initialAppointments,
  onBack,
}: AppointmentModalProps) => {
  const { pushModal } = useModals();
  const { updateToast } = useToast();

  const [state, actions] = useTypesafeActions<
    SearchAppointmentState,
    SearchAppointmentStateActions
  >(
    searchAppointmentStateReducer,
    {
      ...SearchAppointmentStateDefault,
      searchValue: initialSearchTerm,
      initiallySelectedAppointments:
        initialAppointments?.map((a) => String(a.appointmentId)) ?? [],
      selectedAppointment: undefined,
    },
    searchAppointmentStateActions
  );

  const onAssociate = async () => {
    if (state.selectedAppointment !== undefined) {
      try {
        actions.setIsLoading(true);

        if (state.selectedAppointment.opportunityId !== undefined) {
          await dissasociateAppointment(
            state.selectedAppointment?.opportunityId,
            state.selectedAppointment?.id
          );
        }

        await asociateAppointment(projectId, state.selectedAppointment.id);

        updateToast({
          id: state.selectedAppointment?.id,
          title: t("prompt.appointment_saved"),
        });
      } catch (err) {
        updateToast({
          id: state.selectedAppointment?.id,
          title: t("prompt.appointment_saved_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      } finally {
        actions.setIsLoading(false);
        closeModal();
      }
    }
  };

  return (
    <ModalSheet
      title={t("action.search_appointment", {})}
      visible={isOpen}
      onClose={closeModal}
      onBack={onBack}
      primaryButtonProps={{
        text: t("action.add_to_project"),
        disabled: !state.selectedAppointment,
        loading: state.isLoading,
        onClick: () => {
          if (
            state.selectedAppointment &&
            !state.initiallySelectedAppointments?.includes(
              String(state.selectedAppointment.appointmentId)
            )
          ) {
            if (state.selectedAppointment.opportunityId !== undefined) {
              pushModal({
                type: "CONFIRM",
                onConfirm: onAssociate,
                title: t("action.add_to_project"),
                message: t("prompt.confirm_associate_appointment"),
              });
            } else {
              onAssociate();
            }
          }
        },
      }}
    >
      <SearchAppointments
        searchTerm={state.searchValue ?? initialSearchTerm}
        actions={actions}
        state={state}
        closeModal={closeModal}
      />
    </ModalSheet>
  );
};
